import { z } from 'zod';
export const athenaDirectionSchema = z.enum(['face', 'body', 'massage', 'relax', 'unknown']);
export const athenaSpecialistSchema = z.enum(['cosmetologist', 'masseur', 'unknown']);
export const athenaClientStatusSchema = z.enum(['new', 'returning', 'unknown']);
export const athenaConcernSchema = z.enum([
    'dry_skin',
    'breakouts',
    'dull_skin',
    'aging',
    'pores',
    'edema',
    'tension',
    'body_tone',
    'relaxation',
    'unknown',
]);
export const athenaGoalSchema = z.enum([
    'improve_skin_condition',
    'relax',
    'body_care',
    'specific_service',
    'book_appointment',
    'unknown',
]);
export const athenaUnderstandingConfidenceSchema = z.enum(['high', 'medium', 'low']);
/**
 * ITERATION_10 — что клиент СЕЙЧАС хочет от разговора, не про процедуру.
 * describe_need — рассказывает о себе/проблеме (дефолт, как раньше).
 * ask_overview — «какие у вас есть процедуры» — просит обзор направлений, не диагностику.
 * ask_price — «сколько стоит X» — просит цену, не рекомендацию с нуля.
 * ready_to_book — прямо просит запись/время.
 */
export const athenaIntentSchema = z.enum(['describe_need', 'ask_overview', 'ask_price', 'ready_to_book', 'other']);
/** Single-message extraction — safe enums only, no raw text. */
export const athenaMessageUnderstandingSchema = z
    .object({
    intent: athenaIntentSchema.optional(),
    direction: athenaDirectionSchema.optional(),
    specialist: athenaSpecialistSchema.optional(),
    client_status: athenaClientStatusSchema.optional(),
    concerns: z.array(athenaConcernSchema).optional(),
    goal: athenaGoalSchema.optional(),
    known_service_hint: z.string().max(120).optional(),
    ready_for_booking: z.boolean().optional(),
    confidence: athenaUnderstandingConfidenceSchema,
})
    .strict();
/** Internal AI workflow stage — canonical for new sessions; mirrors legacy booking_stage values. */
export const athenaWorkflowStageSchema = z.enum([
    'discovery',
    'clarify',
    'recommendation',
    'safety_gate',
    'safety_ask',
    'safety_details',
    'offer',
    'slots',
    'contact',
    'confirm',
]);
export const athenaSessionMemorySchema = z
    .object({
    direction: athenaDirectionSchema.optional(),
    specialist: athenaSpecialistSchema.optional(),
    client_status: athenaClientStatusSchema.optional(),
    concerns: z.array(athenaConcernSchema).default([]),
    goal: athenaGoalSchema.optional(),
    known_service_hint: z.string().max(120).optional(),
    suggested_service_ids: z.array(z.string().max(32)).optional(),
    /** Legacy bridge — kept in sync on new AI writes; CRM fallback writes legacy-only. */
    booking_stage: athenaWorkflowStageSchema.optional(),
    /** Canonical internal workflow stage for AI runtime (typed-first over booking_stage). */
    workflowStage: athenaWorkflowStageSchema.optional(),
    /**
     * Триггеры Safety Router в рамках текущей сессии (не история клиента).
     * Только enums — без свободного мед. текста.
     */
    safety_triggers: z
        .array(z.enum([
        'retinoids',
        'sensitive_skin',
        'active_inflammation',
        'pregnancy',
        'medication',
        'allergy_mentioned',
        'chronic_skin',
        'recent_procedure',
        'invasive_intent',
    ]))
        .max(12)
        .optional(),
    /** Последний вердикт Safety Router в сессии. */
    safety_route: z.enum(['SAFE', 'NEED_CLARIFICATION', 'REDIRECT_CONSULTATION']).optional(),
    /** Нужен короткий вопрос перед слотами — комментарий мастеру, не фильтр выбора. */
    safety_booking_comment_pending: z.boolean().optional(),
    /** Структурный ответ по safety (без свободного мед. текста). */
    safety_note: z.enum(['none', 'allergy', 'medication', 'chronic', 'sensitive_skin', 'other']).optional(),
    /** Категория выбрана чипом — ждём короткое описание своими словами. */
    safety_pending_note: z
        .enum(['allergy', 'medication', 'chronic', 'sensitive_skin', 'other'])
        .optional(),
    /**
     * Короткие ответы клиента после вопроса про особенности — только для комментария мастеру при записи.
     * Не для аналитики/LLM-диагнозов.
     */
    safety_booking_notes: z.array(z.string().min(1).max(240)).max(6).optional(),
    mode: z.enum(['intelligence', 'fsm_fallback']).default('intelligence'),
    /** ATHENA.1 — last understanding merge (ISO). */
    updatedAt: z.string().datetime().optional(),
    /** ATHENA.1 — increments when facts are replaced (e.g. direction change). */
    revision: z.number().int().nonnegative().optional(),
})
    .strict();
export const athenaRecommendationItemSchema = z
    .object({
    serviceId: z.string().min(1).max(32),
    name: z.string().min(1).max(200),
    priceFrom: z.number().nonnegative().nullable(),
    durationMin: z.number().int().positive().nullable(),
    why: z.string().min(1).max(300),
    priority: z.number().int().min(1).max(10),
})
    .strict();
export const athenaIntelligenceActionSchema = z.enum([
    'recommend',
    'clarify',
    'answer',
    'book_known_service',
    'handoff',
]);
export const athenaUnderstandingLlmOutputSchema = z
    .object({
    intent: athenaIntentSchema.nullable(),
    direction: athenaDirectionSchema.nullable(),
    specialist: athenaSpecialistSchema.nullable(),
    client_status: athenaClientStatusSchema.nullable(),
    concerns: z.array(athenaConcernSchema).max(8),
    goal: athenaGoalSchema.nullable(),
    known_service_hint: z.string().max(120).nullable(),
    ready_for_booking: z.boolean().nullable(),
})
    .strict();
export const athenaResponseLlmOutputSchema = z
    .object({
    reply: z.string().min(1).max(500),
})
    .strict();
//# sourceMappingURL=intelligence.js.map